import type { Lead, LeadPriority, LeadSource, LeadStatus } from "@/domain/models/lead";
import { VEHICLES, formatPrice } from "./vehicles";

export type { Lead, LeadPriority, LeadSource, LeadStatus } from "@/domain/models/lead";

const salespeople = ["Ava Marchetti", "Jonas Weller", "Priya Ramesh", "Marcus Vale"];
const sources: LeadSource[] = [
  "Contact Form",
  "Reservation",
  "Financing",
  "Brochure",
  "WhatsApp",
  "Phone",
  "Email",
];
const statuses: LeadStatus[] = ["New", "Contacted", "Qualified", "Test Drive", "Won", "Lost"];
const priorities: LeadPriority[] = ["Low", "Medium", "High"];
const firstNames = [
  "Elena", "Marcus", "Kenji", "Sophie", "Idris", "Chiara", "Léa", "Tomás",
  "Nadia", "Rafael", "Ingrid", "Omar", "Hannah", "Julien", "Amara", "Viktor",
  "Rosa", "Sebastián", "Yuki", "Aiden",
];
const lastNames = [
  "Kovacs", "Ashford", "Sato", "Lindqvist", "Okafor", "Rossi", "Martel", "Silva",
  "Haddad", "Novak", "Bergström", "Al-Fahim", "Weiss", "Blanchard", "Diallo", "Petrov",
  "Delgado", "Reyes", "Tanaka", "O'Neill",
];

function seedRand(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

export const LEADS: Lead[] = Array.from({ length: 42 }).map((_, i) => {
  const r = seedRand(i + 7);
  const first = firstNames[Math.floor(r() * firstNames.length)];
  const last = lastNames[Math.floor(r() * lastNames.length)];
  const vehicle = VEHICLES[Math.floor(r() * VEHICLES.length)];
  const daysAgo = Math.floor(r() * 45);
  const createdAt = new Date(Date.now() - daysAgo * 86400000).toISOString();
  const status = statuses[Math.floor(r() * statuses.length)];
  return {
    id: `LEAD-${String(1000 + i)}`,
    name: `${first} ${last}`,
    email: `${first}.${last}`.toLowerCase() + "@example.com",
    phone: `+1 415 555 ${String(100 + Math.floor(r() * 899)).padStart(4, "0")}`,
    vehicleId: vehicle.id,
    source: sources[Math.floor(r() * sources.length)],
    status,
    priority: priorities[Math.floor(r() * priorities.length)],
    assignedTo: salespeople[Math.floor(r() * salespeople.length)],
    message:
      "Interested in a viewing this week — could you confirm availability and share the latest service history?",
    createdAt,
    updatedAt: createdAt,
    tags: r() > 0.5 ? ["VIP"] : r() > 0.5 ? ["Trade-in"] : ["Financing"],
    value: Math.round(vehicle.price * (0.9 + r() * 0.2)),
  };
});

// ── Dashboard series ────────────────────────────────────────
const days = 30;
const now = Date.now();
export const dailyTraffic = Array.from({ length: days }).map((_, i) => {
  const r = seedRand(i + 1);
  const base = 800 + Math.sin(i / 4) * 220 + i * 12;
  const visits = Math.round(base + r() * 220);
  const leads = Math.round(visits * (0.04 + r() * 0.03));
  return {
    date: new Date(now - (days - i) * 86400000).toISOString().slice(5, 10),
    visits,
    leads,
    unique: Math.round(visits * 0.72),
  };
});

export const revenueByMonth = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
].map((m, i) => {
  const r = seedRand(i + 20);
  return {
    month: m,
    revenue: Math.round(1_800_000 + r() * 1_400_000 + i * 40_000),
    units: 6 + Math.round(r() * 12),
  };
});

export const brandDistribution = (() => {
  const map = new Map<string, number>();
  VEHICLES.forEach((v) => map.set(v.brand, (map.get(v.brand) ?? 0) + 1));
  return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
})();

export const bodyDistribution = (() => {
  const map = new Map<string, number>();
  VEHICLES.forEach((v) => map.set(v.body, (map.get(v.body) ?? 0) + 1));
  return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
})();

export const topVehicles = VEHICLES.slice()
  .sort((a, b) => b.price - a.price)
  .slice(0, 5)
  .map((v, i) => ({
    id: v.id,
    label: `${v.brand} ${v.model}`,
    image: v.image,
    views: 4200 - i * 480 + Math.round((i + 1) * 137),
    leads: 32 - i * 4,
    price: formatPrice(v.price),
  }));

export const trafficSources = [
  { name: "Organic", value: 42 },
  { name: "Direct", value: 24 },
  { name: "Paid", value: 18 },
  { name: "Social", value: 11 },
  { name: "Referral", value: 5 },
];

export const funnelStages = [
  { stage: "Visitors", value: 24_800 },
  { stage: "Vehicle views", value: 9_640 },
  { stage: "Inquiries", value: 812 },
  { stage: "Test drives", value: 214 },
  { stage: "Closed", value: 68 },
];

export const keywordRankings = [
  { keyword: "luxury car dealership", position: 3, volume: 4400, change: 2 },
  { keyword: "certified pre-owned audi rs7", position: 5, volume: 1900, change: 1 },
  { keyword: "electric performance sedan", position: 8, volume: 2600, change: -2 },
  { keyword: "aston martin vantage price", position: 4, volume: 1200, change: 3 },
  { keyword: "bmw x7 m60i for sale", position: 6, volume: 880, change: 0 },
  { keyword: "polestar 4 dealer", position: 2, volume: 720, change: 4 },
];

export const coreWebVitals = {
  lcp: 1.9,
  inp: 148,
  cls: 0.04,
  ttfb: 320,
};
